import { Trans, t } from "@lingui/macro";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { HCButton } from "@root/components/atoms/HCButton/Component";
import { HCSwitch } from "@root/components/atoms/HCSwitch/Component";
import { CorrectieIcon, DoorgaanIcon } from "@root/icons/Icons";
import { hasValue } from "@root/misc/helpers";
import { cardStyle } from "@root/utils/ui";
import * as React from "react";
import { useParams } from "react-router-dom";
import screenfull from "screenfull";
import { HCAlert } from "../HCAlert/Component";

interface Props {
  title: string;
  nextButtonLabel: string;
  description: JSX.Element;
  prevFn?: () => void;
  nextFn: () => void;
  part?: number;
}

/**
 * Introduction takes care of the starting screen and the instruction pages in tests.
 * Extra care needs to taken when adding a test or page because of implicit behaviour:
 * - When `props.part === 0` we show an `HCAlert` that prompts the user to go fullscreen.
 *   - And also disables the "next page" button.
 * - If there is a `prevFn` available we show a previous button as well.
 */
export default function Introduction(props: Props): JSX.Element {
  const btnRef = React.useRef<HTMLButtonElement>(null);
  const variant = useParams().variant ?? "on-location";
  const _possibleKeys = ["F1", "F2", "F3", "F4", "F5", "F6", "F7", "F8", "F9"];

  const [isFullscreen, setIsFullscreen] = React.useState(
    screenfull.isEnabled && screenfull.isFullscreen,
  );

  React.useEffect(() => {
    if (screenfull.isEnabled) {
      const handleChange = (): void => {
        setIsFullscreen(screenfull.isFullscreen);
      };
      screenfull.on("change", handleChange);
      return () => {
        screenfull.off("change", handleChange);
      };
    }
  }, []);

  const mayContinue = props.part === 0 && !isFullscreen;

  const handleUserKeyPress = React.useCallback(
    (event: KeyboardEvent): void => {
      event.preventDefault();

      if (event.repeat) {
        return;
      }

      if (event.key === "Enter" && !mayContinue) props.nextFn();
      if (event.key === "Backspace" && props.prevFn) props.prevFn();
    },
    [props.nextFn, props.prevFn, mayContinue],
  );

  React.useEffect(() => {
    window.addEventListener("keydown", handleUserKeyPress);
    btnRef.current?.focus();
    return () => {
      window.removeEventListener("keydown", handleUserKeyPress);
    };
  }, [handleUserKeyPress]);

  const handleFullscreenToggle = (event: React.ChangeEvent<HTMLInputElement>): void => {
    if (event.target.checked) {
      screenfull.isEnabled &&
        screenfull.request().then(() => {
          setIsFullscreen(screenfull.isFullscreen);
        });
    } else {
      screenfull.exit().then(() => {
        setIsFullscreen(screenfull.isFullscreen);
      });
    }
  };

  return (
    <Stack
      direction="column"
      justifyContent="space-between"
      alignItems="center"
      spacing={1}
      sx={{ height: "100%", width: "100%", ...cardStyle }}
    >
      <div style={{ width: "100%", color: "var(--color-brand-quaternary-500)" }}>
        <Typography variant="h1" component="h1" color="var(--color-brand-primary-500)">
          {props.title}
        </Typography>
        {props.description}
        {props.part === 0 && (
          <HCAlert kind={isFullscreen ? "important-info" : "important-warning"}>
            <p>
              <Trans id="fullscreen.message">
                The test is best completed without distractions. Therefore, from this point onward
                it is necessary to complete the test in full-screen mode.
              </Trans>
            </p>

            <Typography variant="subtitle1">
              <Trans id="fullscreen.button">Go fullscreen</Trans>
              <HCSwitch checked={isFullscreen} onChange={handleFullscreenToggle} />
            </Typography>
          </HCAlert>
        )}
      </div>

      {props.prevFn !== undefined ? (
        <Stack direction="row" spacing={16} sx={{ justifyContent: "center" }}>
          <HCButton
            state={{
              kind: "secondary",
              maxWidth: "fit-content",
            }}
            ref={btnRef}
            onClick={() => {
              props.prevFn?.();
            }}
            startIcon={variant === "on-location" ? <CorrectieIcon /> : <ChevronLeftIcon />}
          >
            {t({
              id: "nav.prev_instruction.title",
              message: "Vorige instructie",
            })}
          </HCButton>
          <HCButton
            state={{ kind: "primary", maxWidth: "fit-content" }}
            ref={btnRef}
            onClick={() => props.nextFn()}
            endIcon={variant === "on-location" ? <DoorgaanIcon /> : <ChevronRightIcon />}
          >
            {props.nextButtonLabel}
          </HCButton>
        </Stack>
      ) : (
        <HCButton
          state={
            mayContinue
              ? { kind: "disabled", maxWidth: "fit-content" }
              : { kind: "primary", maxWidth: "fit-content" }
          }
          ref={btnRef}
          onClick={() => props.nextFn()}
          endIcon={variant === "on-location" ? <DoorgaanIcon /> : <ChevronRightIcon />}
        >
          {props.nextButtonLabel}
        </HCButton>
      )}
    </Stack>
  );
}
